import { useState, useEffect, useRef, useCallback } from 'react';
import { type Table } from '@tanstack/react-table';
import { exportCustom } from './tableExport';

interface Props {
  open: boolean;
  onClose: () => void;
  table: Table<any>;
  filename?: string;
}

type RowSource = 'all' | 'selected' | 'page';
type Format = 'csv' | 'xlsx';

export function CustomExportDialog({
  open,
  onClose,
  table,
  filename = 'data-vault-export',
}: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const columns = table.getVisibleLeafColumns();
  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([]);
  const [rowSource, setRowSource] = useState<RowSource>('all');
  const [format, setFormat] = useState<Format>('csv');
  const [customFilename, setCustomFilename] = useState(filename);

  // Sync defaults when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedColumnIds(columns.map((c) => c.id));
      setRowSource('all');
      setFormat('csv');
      setCustomFilename(filename);
    }
  }, [open]);

  // Control native dialog open/close
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const toggleColumn = useCallback((id: string) => {
    setSelectedColumnIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedColumnIds((prev) =>
      prev.length === columns.length ? [] : columns.map((c) => c.id),
    );
  }, [columns]);

  const handleExport = () => {
    if (selectedColumnIds.length === 0) return;
    exportCustom(table, {
      columnIds: selectedColumnIds,
      rowSource,
      format,
      filename: customFilename || filename,
    });
    onClose();
  };

  const getHeaderLabel = (col: (typeof columns)[number]): string => {
    const h = col.columnDef.header;
    return typeof h === 'string' ? h : col.id;
  };

  const selectedRowCount = table.getSelectedRowModel().rows.length;

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="fixed inset-0 z-50 bg-transparent p-0 m-auto backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <div className="bg-mono-900 border border-mono-700 rounded-xl p-6 shadow-2xl max-w-md w-full">
        {/* Header */}
        <h2 className="text-sm font-medium text-mono-100 tracking-wider mb-5">
          EXPORT DATA
        </h2>

        {/* Filename */}
        <div className="mb-4">
          <label className="block text-xs text-mono-300 mb-1.5">
            Filename
          </label>
          <input
            type="text"
            value={customFilename}
            onChange={(e) => setCustomFilename(e.target.value)}
            className="w-full bg-mono-800 border border-mono-700 rounded px-3 py-1.5 text-xs text-mono-100 outline-none focus:border-cyan-600 transition-colors"
          />
        </div>

        {/* Format */}
        <div className="mb-4">
          <label className="block text-xs text-mono-300 mb-1.5">Format</label>
          <div className="flex gap-3">
            {(['csv', 'xlsx'] as const).map((f) => (
              <label
                key={f}
                className="flex items-center gap-1.5 cursor-pointer"
              >
                <input
                  type="radio"
                  name="format"
                  value={f}
                  checked={format === f}
                  onChange={() => setFormat(f)}
                  className="accent-cyan-600"
                />
                <span className="text-xs text-mono-300 uppercase">{f}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Row Source */}
        <div className="mb-4">
          <label className="block text-xs text-mono-300 mb-1.5">Rows</label>
          <div className="flex flex-col gap-1.5">
            {(
              [
                ['all', 'All filtered rows'],
                ['selected', `Selected rows only (${selectedRowCount})`],
                ['page', 'Current page only'],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className="flex items-center gap-1.5 cursor-pointer"
              >
                <input
                  type="radio"
                  name="rowSource"
                  value={value}
                  checked={rowSource === value}
                  onChange={() => setRowSource(value)}
                  className="accent-cyan-600"
                />
                <span className="text-xs text-mono-300">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Columns */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs text-mono-300">Columns</label>
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-cyan-500 hover:text-cyan-400 transition-colors"
            >
              {selectedColumnIds.length === columns.length
                ? 'Deselect all'
                : 'Select all'}
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto border border-mono-700 rounded bg-mono-800 p-2 space-y-1">
            {columns.map((col) => (
              <label
                key={col.id}
                className="flex items-center gap-2 cursor-pointer hover:bg-mono-700/40 rounded px-1 py-0.5"
              >
                <input
                  type="checkbox"
                  checked={selectedColumnIds.includes(col.id)}
                  onChange={() => toggleColumn(col.id)}
                  className="accent-cyan-600"
                />
                <span className="text-xs text-mono-300 truncate">
                  {getHeaderLabel(col)}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-mono-400 hover:text-mono-200 px-4 py-2 text-xs tracking-wider transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={selectedColumnIds.length === 0}
            className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded text-xs tracking-wider transition-colors"
          >
            Export
          </button>
        </div>
      </div>
    </dialog>
  );
}
